// Fatia S1: quem acessa a empresa, convites e papéis (inclui cliente da agência).
import { useQueryClient } from '@tanstack/react-query'
import type { CompanyRole } from '@impulso/shared'
import { api, errorMessage } from '../../lib/api'
import { useCan, useCompany } from '../../lib/company'
import { useSession } from '../../lib/session'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { Empty, ErrorState, Loading, PageHead, Switch } from '../../ui'
import CompanySettingsTabs from '../../features/account/CompanySettingsTabs'
import InviteForm from '../../features/account/InviteForm'
import { useTeam } from '../../features/account/api'
import { ACCOUNT_ROLE_LABEL, COMPANY_ROLE_LABEL, dateBR, initials } from '../../features/account/labels'

export default function SettingsTeam() {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const { me } = useSession()
  const qc = useQueryClient()
  const toast = useToast()
  const confirm = useConfirm()
  const team = useTeam(company.id)
  if (!canEdit) return <><PageHead title="Configurações" /><CompanySettingsTabs /><Empty title="Só gestores podem ver e convidar a equipe." /></>

  const account = me?.accounts.find((a) => a.id === company.accountId)
  const canBill = !!account?.permissions.includes('account.billing')
  const reload = () => qc.invalidateQueries({ queryKey: ['team', company.id] })

  async function act(fn: () => Promise<unknown>, msg: string) {
    try { await fn(); toast(msg); await reload() } catch (err) { toast(errorMessage(err)) }
  }
  const setGrant = (userId: string, role: CompanyRole, clientCanApprove: boolean) =>
    act(() => api.put(`/companies/${company.id}/grants/${userId}`, { role, clientCanApprove }), 'Acesso atualizado.')
  const removeGrant = async (userId: string, name: string) => {
    if (!(await confirm({ title: `Remover o acesso de ${name}?`, message: `A pessoa deixa de ver ${company.name}.`, confirmLabel: 'Remover', danger: true }))) return
    void act(() => api.del(`/companies/${company.id}/grants/${userId}`), 'Acesso removido.')
  }
  const revoke = (id: string) => act(() => api.del(`/accounts/${company.accountId}/invitations/${id}`), 'Convite cancelado.')

  return (
    <>
      <PageHead title="Configurações" sub={`Quem pode ver e mexer nos anúncios de ${company.name}.`} />
      <CompanySettingsTabs />
      <div className="grid g-main">
        <div className="card">
          <h2>Quem acessa</h2>
          <div className="list mt-16">
            {team.isLoading && <Loading lines={3} />}
            {team.error && <ErrorState error={team.error} onRetry={() => team.refetch()} />}
            {team.data?.items.map((m) => {
              const self = m.userId === me?.user.id
              return (
                <div key={m.userId} className="list-item" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                  <span className="avatar sm">{initials(m.name)}</span>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <div className="strong">{m.name}{self && <span className="muted"> (você)</span>}</div>
                    <div className="small muted">{m.email}</div>
                  </div>
                  {m.source === 'account' ? (
                    <span className="badge purple">{ACCOUNT_ROLE_LABEL[m.accountRole ?? 'admin']} · todas as empresas</span>
                  ) : (
                    <div className="row wrap" style={{ gap: 8 }}>
                      <select className="select" aria-label={`Papel de ${m.name}`} style={{ width: 'auto' }} value={m.role} disabled={self}
                        onChange={(e) => setGrant(m.userId, e.target.value as CompanyRole, m.clientCanApprove)}>
                        {(['manager', 'analyst', 'viewer', ...(team.data?.canInviteClient || m.role === 'client' ? ['client'] : [])] as CompanyRole[]).map((r) => <option key={r} value={r}>{COMPANY_ROLE_LABEL[r]}</option>)}
                      </select>
                      {m.role === 'client' && (
                        <label className="row small" style={{ gap: 6 }}>
                          <Switch on={m.clientCanApprove} label="Pode aprovar sugestões" onChange={(v) => setGrant(m.userId, 'client', v)} /> Aprova sugestões
                        </label>
                      )}
                      {!self && <button className="btn sm ghost" onClick={() => removeGrant(m.userId, m.name)}>Remover</button>}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
          {team.data && team.data.invitations.length > 0 && (
            <>
              <div className="divider" />
              <h3>Convites pendentes</h3>
              <div className="list mt-12">
                {team.data.invitations.map((i) => (
                  <div key={i.id} className="list-item" style={{ alignItems: 'center' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="strong" style={{ overflowWrap: 'anywhere' }}>{i.email}</div>
                      <div className="small muted">{i.companyRole ? COMPANY_ROLE_LABEL[i.companyRole] : ''} · {i.expired ? 'expirou' : `vale até ${dateBR(i.expiresAt)}`}</div>
                    </div>
                    <button className="btn sm ghost" onClick={() => revoke(i.id)}>Cancelar</button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className="card">
          <h2>Convidar alguém</h2>
          <p className="small muted mt-8">A pessoa recebe um e-mail e cria a senha dela. Ela só vai ver esta empresa.</p>
          <div className="mt-16">
            <InviteForm mode="company" accountId={company.accountId} companyId={company.id} canInviteClient={!!team.data?.canInviteClient} canBill={canBill} onDone={reload} />
          </div>
        </div>
      </div>
    </>
  )
}
